/* eslint-disable max-len,no-underscore-dangle */
import { i18n } from 'vue-infra';
import { each, get, isNumber, map, cloneDeep, merge, set, find, isNull, includes, isEqual, isEmpty, pickBy, values as getValues, some, isUndefined, isNil } from 'lodash';
import moment from 'moment';
import {
  DEFAULT_TIMEFRAME_DAYS_VALUE,
  AXIOS_RETRY_OBJECT,
  RETRIES_MAX_NUMBER,
  TIMEFRAME_FILTER_TYPES,
  DEFAULT_TIMEFRAME_RELATIVE_HOUR_VALUE,
  DEFAULT_TIMEFRAME_ABSOLUTE_HOUR_VALUE,
  MAX_AMW_VERSION_NUMBER,
  NUMBER_OF_FILTER_MAX_ITEMS_CHARACTERS,
  SITE_SETTINGS_VERSION_KEY,
  SESSION_SETTINGS,
  METRIC_NAMES,
  ALERT_CONDITION_TYPES,
  VALIDATION_TYPES,
  DURATION_TIME_UNITS,
  METRIC_UNITS,
} from '@config/Consts';
import { ALERT_CONFIG } from '@config/AlertConfig';
import { metricsConfig as METRIC_DETAILS_PER_WIDGET } from '@config/metricsConfig';
import ApiResources from '@core/data/apiResources';
import AlertBam from '@core/bam/AlertBam'; // TODO refactor


const MAP_UNIT_I18N = {
  [DURATION_TIME_UNITS.HOURS]: 'amd:time_unit_hour_long',
  [DURATION_TIME_UNITS.MINUTES]: 'amd:time_unit_minute_long',
  [DURATION_TIME_UNITS.SECONDS]: 'amd:time_unit_second_long',
};

const PERCENT_SIGN_I18N = 'amd:alerts_configuration_percent_sign';

const getAgentMetricEmptyValue = (metricName) => {
  const ZERO_VALUES_METRICS = [
    METRIC_NAMES.ONLINE_RATE,
    METRIC_NAMES.AGENT_VIEW_ASSIGNED_CONVERSATIONS,
    METRIC_NAMES.AGENT_VIEW_ACTIVE_CONVERSATIONS,
    METRIC_NAMES.AGENT_VIEW_MAX_SLOTS,
    METRIC_NAMES.AGENT_VIEW_AGENT_LOAD,
    METRIC_NAMES.AGENT_VIEW_CLOSED_CONVERSATIONS,
  ];

  return includes(ZERO_VALUES_METRICS, metricName) ? '0' : i18n.t('amd:missing_value_na');
};

const getItemsFromMetricScore = (metricScore, valueAttr) => {
  if (valueAttr) {
    return map(metricScore, valueAttr);
  }
  return metricScore;
};

const getApiTimeParams = (timeFrame) => {
  const type = get(timeFrame, 'type') || TIMEFRAME_FILTER_TYPES.RELATIVE;
  const from = moment().subtract(DEFAULT_TIMEFRAME_DAYS_VALUE, 'days').format('x');
  const to = moment().format('x'); // will format in timestamp
  let lastUpdateFrom;
  if (type === TIMEFRAME_FILTER_TYPES.RELATIVE) {
    const relativeHour = get(timeFrame, 'relativeHour') || DEFAULT_TIMEFRAME_RELATIVE_HOUR_VALUE;
    lastUpdateFrom = moment().subtract(relativeHour, 'hours').format('x');
  } else {
    const absoluteHour = get(timeFrame, 'absoluteHour') || DEFAULT_TIMEFRAME_ABSOLUTE_HOUR_VALUE;
    lastUpdateFrom = moment().startOf('day').add(absoluteHour, 'hours').format('x');
  }
  return { from, to, lastUpdateFrom };
};

const convertTimeFrameToMinutes = (timeFrame) => {
  const type = get(timeFrame, 'type') || TIMEFRAME_FILTER_TYPES.RELATIVE;
  let result;
  // The value should be in minutes
  if (type === TIMEFRAME_FILTER_TYPES.RELATIVE) {
    const relativeHour = get(timeFrame, 'relativeHour') || DEFAULT_TIMEFRAME_RELATIVE_HOUR_VALUE;
    result = relativeHour * 60;
  } else {
    const absoluteHour = get(timeFrame, 'absoluteHour') || DEFAULT_TIMEFRAME_ABSOLUTE_HOUR_VALUE;
    const time = moment().startOf('day').add(absoluteHour, 'hours');
    result = moment().diff(time, 'minutes');
  }
  return result;
};

const formatTimeFrameHour = hourNumber => moment().startOf('day').add(hourNumber, 'hours').format('LT');

const getSiteSettingValue = (options) => {
  const value = get(options, 'siteSetting.propertyValue.value', options.defaultValue);
  return options.isBoolean ? (value === 'true') : value;
};

const isMaxNumRetriesReached = (err) => {
  const numRetries = err && err.config && err.config[AXIOS_RETRY_OBJECT]
    && err.config[AXIOS_RETRY_OBJECT].retryCount;
  return numRetries && numRetries >= RETRIES_MAX_NUMBER;
};

const extractVersionNumber = version =>
  (version.indexOf('-') > -1 ? version.substr(0, version.indexOf('-')) : version);

/**
 * convert version format to version number
 * the version format is X.Y.Z and they are mandatory
 * we assume that X, Y, Z < 100
 * example 5.3.15 will be converted to 5,003,015
 * @param version
 * @returns {number}
 */
const convertVersionToNumber = (version) => {
  let result = MAX_AMW_VERSION_NUMBER;
  if (isNumber(version)) {
    const maxPortNumber = 65535; // (in dev mode, version is the port number)
    result = version <= maxPortNumber ? MAX_AMW_VERSION_NUMBER : version;
  } else if (version && typeof version === 'string' && version.length > 0) {
    const coefficientMap = [6, 3, 0];
    const leftPart = extractVersionNumber(version);
    if (leftPart && leftPart.indexOf('.') > -1) {
      const versionParts = leftPart.split('.');
      result = 0;
      for (let i = 0; i < versionParts.length; i += 1) {
        const coef = coefficientMap[i];
        result += (versionParts[i] * (10 ** (coef)));
      }
    }
  }
  return result;
};

const calculateFilterMaxPreviewItems = (numberOfItems, firstItem, secondItem) => {
  // We need to check whether the sum of characters
  // in the filter entity names is not above the maximum
  let numberOfItemsToDisplay;
  let isLongItemsNames;
  if (numberOfItems === 0 || numberOfItems === 1) {
    numberOfItemsToDisplay = numberOfItems;
    isLongItemsNames = numberOfItems === 1 ?
      firstItem.length >= NUMBER_OF_FILTER_MAX_ITEMS_CHARACTERS : false;
  } else {
    const totalCharacters = firstItem.length + secondItem.length;
    numberOfItemsToDisplay = (totalCharacters >= NUMBER_OF_FILTER_MAX_ITEMS_CHARACTERS) ? 1 : 2;
    isLongItemsNames = totalCharacters >= NUMBER_OF_FILTER_MAX_ITEMS_CHARACTERS;
  }
  return { numberOfItemsToDisplay, isLongItemsNames };
};

const getLastTimeUpdated = (lastTimeDataUpdated, isTimestamp = false) => {
  let result = isTimestamp ? 0 : '--';
  if (!isNull(lastTimeDataUpdated)) {
    result = isTimestamp ? lastTimeDataUpdated.format('x') : lastTimeDataUpdated.calendar();
  }
  return result;
};

const makeArrayOfStrings = list => map(list, item => item.toString());
// We go over force_update_params and overwrite the specific attributes in resultSettings
// with the corresponding value from originalSettings
const overwriteSiteSettingParams = (resultSettings, originalSettings, overwriteParams) => {
  overwriteParams.forEach((overwriteAttribute) => {
    const valueToOverwrite = get(originalSettings, overwriteAttribute);
    set(resultSettings, overwriteAttribute, valueToOverwrite);
  });
};

const mergeSiteSettingConfiguration = (defaultSettings, accountSettings, newVersion) => {
  const mergedConfigurationVersion = newVersion.numeric !== MAX_AMW_VERSION_NUMBER ?
    newVersion.value : defaultSettings.AMW_version_from;
  const mergedConfiguration = {
    [SITE_SETTINGS_VERSION_KEY]: mergedConfigurationVersion,
    configuration: [],
  };
  mergedConfiguration.configuration =
    merge(cloneDeep(defaultSettings.configuration), accountSettings.configuration);
  overwriteSiteSettingParams(mergedConfiguration, defaultSettings,
    defaultSettings.force_update_params);
  return mergedConfiguration;
};

// This method converts site-settings metrics configuration to boolean
// and vice versa as site-setting only supports String and not Boolean
const parseMetricsConfiguration = (configuration, convertToBoolean = true) => {
  const configBooleanKeys = ['config.enabled', 'config.configurable', 'config.deleted', 'config.ACFeatureEnabled'];
  configuration.forEach((widgetConfiguration, widgetIndex) => {
    configuration[widgetIndex].metrics.forEach((metricConfiguration, metricIndex) => {
      configBooleanKeys.forEach((attributeKey) => {
        let attributeValue =
          get(configuration[widgetIndex].metrics[metricIndex], attributeKey);
        if (attributeValue !== undefined) {
          attributeValue = convertToBoolean ?
            attributeValue === 'true' : attributeValue.toString();
          set(configuration[widgetIndex].metrics[metricIndex], attributeKey, attributeValue);
        }
      });
    });
  });
  return configuration;
};

/* eslint-disable no-param-reassign */
const getInterceptor = (getters, request) => {
  const timeFrameFilter = getters.getTimeFrameFilter;
  if (get(request, 'data.filters.time.from') && get(request, 'data.filters.time.to')) {
    const apiTimeParams = getApiTimeParams(timeFrameFilter);
    request.data.filters.time.from = Number(apiTimeParams.lastUpdateFrom);
    request.data.filters.time.to = Number(apiTimeParams.to);
  } else if (get(request, 'data.timeframe')) {
    const apiTimeInMimutes = convertTimeFrameToMinutes(timeFrameFilter);
    request.data.timeframe = Number(apiTimeInMimutes);
  }
  // For rollover account add rollover param to metrics, bots agent_view and bots historical APIs
  if (!!getters.hasContactCenterFeature && request.baseURL) {
    const urlResolver = window.LE.urlResolver;
    const baseURLs = {
      metricsBaseURL: urlResolver.getUrl(get(ApiResources, 'AgentManagerData.Metrics')),
      agentViewBaseURL: urlResolver.getUrl(get(ApiResources, 'AgentManagerData.AgentView')),
      historicalBaseURL: urlResolver.getUrl(get(ApiResources, 'AgentManagerData.Historical')),
    };
    if (request.baseURL.indexOf(baseURLs.metricsBaseURL) > -1 ||
      request.baseURL.indexOf(baseURLs.agentViewBaseURL) > -1 ||
      request.baseURL.indexOf(baseURLs.historicalBaseURL) > -1) {
      set(request, 'params.rollover', true);
    }
  }
  return request;
};

const extractConfigurationFromSiteSetting = (siteSettingsArray) => {
  const metricsConfigurationSiteSetting =
    find(siteSettingsArray,
      siteSetting => siteSetting.id === SESSION_SETTINGS.MANAGER_WORKSPACE_METRICS_CONFIGURATION);
  const siteSettingValue = get(metricsConfigurationSiteSetting, 'propertyValue.value');
  const metricsConfiguration = get(siteSettingValue, 'configuration');
  const metricsConfigurationVersion = get(siteSettingValue, 'AMW_version');
  return { metricsConfiguration, metricsConfigurationVersion };
};

/**
 * Check a required feature then enable/disable the match metric.
 * If the the relevant feature is not equal to the metric ACFeatureEnabled attribute,
 * the siteSetting need to be updated with the right setting for the metric
 * @param {*} siteSettingConfiguration - updated SiteSettingConfiguration to work on
 */
const updateSiteSettingsPerSpecificFeature =
  (siteSettingConfiguration, widgetNames, metricNames, hasFeature, isConfigurable = false) => {
    let updateNeeded = false;
    widgetNames.forEach((widgetName) => {
      const widgetIndex = siteSettingConfiguration.configuration.findIndex(
        widget => widget.widget_name === widgetName);
      if (widgetIndex > -1) {
        metricNames.forEach((metricName) => {
          const metricIndex =
            siteSettingConfiguration.configuration[widgetIndex].metrics.findIndex(
              metric => metric.name === metricName);
          if (metricIndex > -1) {
            const metric =
              siteSettingConfiguration.configuration[widgetIndex].metrics[metricIndex];
            if (metric) {
              const metricACFeatureEnabled = get(metric, 'config.ACFeatureEnabled');
              if (hasFeature !== metricACFeatureEnabled) {
                if (hasFeature) {
                  set(metric, 'config.enabled', true);
                  set(metric, 'config.ACFeatureEnabled', true);
                  set(metric, 'config.configurable', isConfigurable);
                } else { // If the AC Feature changed to false, disable the metric's column
                  set(metric, 'config.enabled', false);
                  set(metric, 'config.ACFeatureEnabled', false);
                  set(metric, 'config.configurable', false);
                }
                updateNeeded = true;
              }
            }
          }
        });
      }
    });
    return updateNeeded;
  };

const getMetricLabel = (widgetName, metricName) => {
  const widgetMetrics = Object.values(METRIC_DETAILS_PER_WIDGET[widgetName]);
  const requestedMetric = find(widgetMetrics, metric => metric.metricName === metricName);
  return requestedMetric && requestedMetric.metricLabelI18nKey ? i18n.t(`amd:${requestedMetric.metricLabelI18nKey}`) : null;
};

const getMetricSettingsKey = (widgetName, metricName) => {
  let requestedMetricKey = null;
  const widgetMetricsKeys = Object.keys(METRIC_DETAILS_PER_WIDGET[widgetName]);
  widgetMetricsKeys.forEach((key) => {
    if (METRIC_DETAILS_PER_WIDGET[widgetName][key].metricName === metricName) {
      requestedMetricKey = key;
    }
  });
  return requestedMetricKey;
};

const sortArrayComparator = fieldName => (a, b) => {
  if (a[fieldName].toLowerCase() === b[fieldName].toLowerCase()) {
    return 0;
  } else if (a[fieldName].toLowerCase() < b[fieldName].toLowerCase()) {
    return -1;
  }
  return 1;
};

/*
example of options:
{
          flatIdFieldName: 'id',
          flatNameFieldName: 'name',
          flatParentIdName: 'parentGroupId',
          treeIdFieldName: 'id',
          treeNameFieldName: 'name',
          treeChildrenFieldName: 'children',
        }
 */
const makeTree = (flatData, options, defaultRootId, defaultRootName) => {
  // first map to the relevant fields only
  const data = map(flatData, item => ({
    [options.flatIdFieldName]: item[options.flatIdFieldName],
    [options.flatParentIdName]: item[options.flatParentIdName],
    [options.flatParentIdName]: item[options.flatParentIdName],
    [options.flatNameFieldName]: item[options.flatNameFieldName],
  }));
  const tree = [];
  const hash = {};
  // build a hash table based on all the items
  each(data, (item) => {
    hash[item[options.flatIdFieldName]] = {
      [options.treeIdFieldName]: item[options.flatIdFieldName],
      [options.flatParentIdName]: item[options.flatParentIdName],
      [options.treeNameFieldName]: item[options.flatNameFieldName],
      [options.treeChildrenFieldName]: [],
    };
  });

  if (!hash[defaultRootId]) {
    hash[defaultRootId] = {
      [options.flatIdFieldName]: defaultRootId,
      [options.flatParentIdName]: undefined,
      [options.treeNameFieldName]: defaultRootName,
      [options.treeChildrenFieldName]: [],
    };
  }

  // go over all the items and find corresponding parent item and append the item's children field
  each(Object.keys(hash), (id) => {
    const parentId = get(hash[id], options.flatParentIdName);
    const parentItem = hash[parentId];
    if (parentId && parentItem) {
      const children = parentItem[options.treeChildrenFieldName];
      children.push(hash[id]);
      children.sort(sortArrayComparator(options.flatNameFieldName));
    } else if (!parentId) {
      // When there is no parent Id it's the root item
      tree.push(hash[id]);
    } else {
      // When there is a parent Id but the parent item was not found
      // it's because the user is not allowed to see the parent group.
      // In this case we will assign the item to the root item directly.
      const children = hash[defaultRootId][options.treeChildrenFieldName];
      children.push(hash[id]);
      children.sort(sortArrayComparator(options.flatNameFieldName));
      set(hash[id], options.flatParentIdName, defaultRootId);
    }
  });
  return tree;
};

const getSubscriptionsByMode = (subscriptions, SUBSCRIPTION_BY_MODE, mode) => {
  const result = [];
  if (subscriptions && subscriptions.subscriptions) {
    each(subscriptions.subscriptions, (subscription) => {
      if (includes(SUBSCRIPTION_BY_MODE[mode], subscription.name)) {
        result.push(subscription);
      }
    });
  }
  return result;
};

const getServiceBaseURI = (serviceName, csdsRecords) => {
  const service = find(csdsRecords, item => item.service === serviceName);
  return service ? service.baseURI : null;
};

const useScrolling = (element) => {
  const { scrollTop, scrollHeight, offsetHeight } = element;
  const isAtScrollEnd = scrollTop >= (scrollHeight - offsetHeight) * 0.93;
  return {
    isAtScrollEnd,
    scrollTop: element.scrollTop,
  };
};

const lowerCaseCompare = (text, other) => isEqual(text.toLowerCase(), other.toLowerCase());

const convertToDuration = (durationObj) => {
  if (isEmpty(durationObj)) {
    return null;
  }
  const duration = moment.duration({ [durationObj.unit]: durationObj.duration });
  return duration._milliseconds;
};

const getValuesByType = (validationType, values) => {
  let valuesObj = { singleValue: values.singleValue, rangeFromValue: values.rangeFromValue, rangeToValue: values.rangeToValue };
  if (validationType === VALIDATION_TYPES.DURATION) {
    const singleValue = convertToDuration(values.singleValue);
    const rangeFromValue = convertToDuration(values.rangeFromValue);
    const rangeToValue = convertToDuration(values.rangeToValue);
    valuesObj = { singleValue, rangeFromValue, rangeToValue };
  }
  return valuesObj;
};

const getDisplayedAlertValueString = (validationType, value) => {
  let result = value;
  if (validationType === VALIDATION_TYPES.DURATION) {
    result = `${value.duration} ${i18n.t(MAP_UNIT_I18N[value.unit])}`;
  } else if (validationType === VALIDATION_TYPES.PERCENTAGE) {
    result = `${value} ${i18n.t(PERCENT_SIGN_I18N)}`;
  }
  return result;
};

const metricAlertData = (metricAlert, score, validation, metricName) => {
  let showAlert = false;
  let tooltip = '';
  const conditionType = get(metricAlert, 'conditionType');
  if (!isEmpty(conditionType)) {
    const metricAlertValues = get(metricAlert, 'values');
    const values = getValuesByType(validation.type, metricAlertValues);
    score = !isNil(score) ? score : null;
    score = validation.type === VALIDATION_TYPES.PERCENTAGE ? Number(score) * 100 : Number(score);
    if (conditionType === ALERT_CONDITION_TYPES.ABOVE) {
      showAlert = score > Number(values.singleValue);
      tooltip = showAlert ? i18n.t('amd:metric_alert_above', { metricName, value: getDisplayedAlertValueString(validation.type, metricAlert.values.singleValue) }) : i18n.t('amd:metric_alert_ended', { metricName });
    } else if (conditionType === ALERT_CONDITION_TYPES.BELOW) {
      showAlert = score < Number(values.singleValue);
      tooltip = showAlert ? i18n.t('amd:metric_alert_below', { metricName, value: getDisplayedAlertValueString(validation.type, metricAlert.values.singleValue) }) : i18n.t('amd:metric_alert_ended', { metricName });
    } else if (conditionType === ALERT_CONDITION_TYPES.BETWEEN) {
      showAlert = (score >= Number(values.rangeFromValue) &&
        score <= Number(values.rangeToValue));
      tooltip = showAlert ? i18n.t('amd:metric_alert_between', { metricName, from: getDisplayedAlertValueString(validation.type, metricAlert.values.rangeFromValue), to: getDisplayedAlertValueString(validation.type, metricAlert.values.rangeToValue) }) : i18n.t('amd:metric_alert_ended', { metricName });
    }
  }
  return { showAlert, tooltip };
};

const isWithinTimeframe = (value) => {
  const startTime = moment().startOf('day').add(value.startTimeValue.valueAbsMinutes, 'minutes');
  const endTime = moment().startOf('day').add(value.endTimeValue.valueAbsMinutes, 'minutes');
  return moment().isAfter(startTime) && moment().isBefore(endTime);
};

const timeframeMetricAlertData = (metricAlert, score, validation, nameI18n) => {
  const metricName = i18n.t(`amd:${nameI18n}`);
  const defaultTooltip = i18n.t('amd:metric_alert_ended', { metricName });
  const values = get(metricAlert, 'values.timeframeValues');
  let showAlert = false;
  let tooltip;
  some(values, (value) => {
    const conditionType = value.conditionType.value;
    const isInTimeframe = isWithinTimeframe(value);
    if (conditionType === ALERT_CONDITION_TYPES.ABOVE) {
      showAlert = isInTimeframe && score > Number(value.thresholdValueFrom);
      tooltip = showAlert ? i18n.t('amd:metric_alert_above', { metricName,
        value: getDisplayedAlertValueString(validation.type, value.thresholdValueFrom) }) : defaultTooltip;
    } else if (conditionType === ALERT_CONDITION_TYPES.BELOW) {
      showAlert = isInTimeframe && score < Number(value.thresholdValueFrom);
      tooltip = showAlert ? i18n.t('amd:metric_alert_below', { metricName,
        value: getDisplayedAlertValueString(validation.type, value.thresholdValueFrom) }) : defaultTooltip;
    } else if (conditionType === ALERT_CONDITION_TYPES.BETWEEN) {
      showAlert = isInTimeframe && (score >= Number(value.thresholdValueFrom) && score <= Number(value.thresholdValueTo));
      tooltip = showAlert ? i18n.t('amd:metric_alert_between', { metricName,
        from: getDisplayedAlertValueString(validation.type, value.thresholdValueFrom),
        to: getDisplayedAlertValueString(validation.type, value.thresholdValueTo) }) : defaultTooltip;
    }
    // stop iteration on first alert found
    return showAlert;
  });
  return { showAlert, tooltip };
};

const getDialogAlertItemsByWidget = (metrics, widgetName, alertData) => {
  const alertConfigItems = pickBy(ALERT_CONFIG[widgetName], (value, key) => includes(metrics, key));
  const dialogAlertItems = [];
  const size = isEmpty(alertConfigItems) ? {} : getValues(alertConfigItems)[0].size;
  each(alertConfigItems, (item, metricName) => {
    item.metricName = metricName;
    item.conditionType = get(alertData, `${metricName}.conditionType`);
    item.values = get(alertData, `${metricName}.values`);
    dialogAlertItems.push(item);
  });
  return { list: dialogAlertItems, size, widgetName };
};

const sendThresholdEvent = (settings) => {
  const options = {
    widgetName: settings.widgetName,
    metricName: settings.metricName,
    values: {
      metricScore: settings.metricScore,
      alertData: settings.alertData,
    },
  };
  AlertBam.sendThresholdReachBam(options);
};

const getMetricScoreWithUnit = (score, unitType) => {
  let result = score.toString();
  if (METRIC_UNITS[unitType]) {
    result = `${score}${METRIC_UNITS[unitType]}`;
  }
  return result;
};

const isEventRelevant = (widget, widgets) => isUndefined(widgets) || (!isUndefined(widgets) && includes(widgets, widget));

export {
  getAgentMetricEmptyValue,
  getItemsFromMetricScore,
  getApiTimeParams,
  isMaxNumRetriesReached,
  convertVersionToNumber,
  convertTimeFrameToMinutes,
  formatTimeFrameHour,
  makeArrayOfStrings,
  calculateFilterMaxPreviewItems,
  mergeSiteSettingConfiguration,
  overwriteSiteSettingParams,
  parseMetricsConfiguration,
  extractConfigurationFromSiteSetting,
  extractVersionNumber,
  getLastTimeUpdated,
  updateSiteSettingsPerSpecificFeature,
  getMetricLabel,
  getMetricSettingsKey,
  getSiteSettingValue,
  makeTree,
  getInterceptor,
  getSubscriptionsByMode,
  getServiceBaseURI,
  useScrolling,
  lowerCaseCompare,
  getMetricScoreWithUnit,
  metricAlertData,
  timeframeMetricAlertData,
  getDialogAlertItemsByWidget,
  sendThresholdEvent,
  isEventRelevant,
};
